import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, AlertCircle, Download, ExternalLink } from 'lucide-react';
/**
 * Public ticket viewer.
 *
 * URL: /ticket/:orderNumber
 *
 * Purpose: Provide a SHORT, predictable, on-domain URL for the kitchen ticket
 * image. WhatsApp on some Android phones occasionally mis-parses very long
 * Cloudinary URLs, causing the link to open the bare host. By hosting the
 * viewer on noizacr.com we eliminate that failure mode for restaurant owners.
 */
export function TicketPage() {
  const {
    orderNumber
  } = useParams<{
    orderNumber: string;
  }>();
  const [ticketUrl, setTicketUrl] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retried, setRetried] = useState(0);
  useEffect(() => {
    let mounted = true;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    async function load() {
      if (!orderNumber) {
        if (mounted) {
          setError('Número de orden no encontrado');
          setLoading(false);
        }
        return;
      }
      try {
        const {
          data,
          error: fetchErr
        } = await supabase.from('orders').select('ticket_image_url, restaurants(name)').eq('order_number', orderNumber).maybeSingle();
        if (fetchErr) throw fetchErr;
        if (!data) {
          if (mounted) {
            setError('No encontramos este pedido');
            setLoading(false);
          }
          return;
        }
        const url = (data as any).ticket_image_url as string | null;
        const rName = (data as any).restaurants?.name || null;
        if (!mounted) return;
        if (url) {
          setTicketUrl(url);
          setRestaurantName(rName);
          setLoading(false);
        } else if (retried < 6) {
          // Ticket image upload is async — retry every 2.5s up to 6 times (~15s).
          timeout = setTimeout(() => {
            if (mounted) setRetried(c => c + 1);
          }, 2500);
        } else {
          setRestaurantName(rName);
          setError('El ticket aún se está generando. Vuelve a intentar en unos segundos.');
          setLoading(false);
        }
      } catch (err: any) {
        if (mounted) {
          setError(err?.message || 'Error al cargar el ticket');
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      mounted = false;
      if (timeout) clearTimeout(timeout);
    };
  }, [orderNumber, retried]);
  const handleDownload = () => {
    if (!ticketUrl) return;
    // Force download on mobile by opening in new tab. The user can long-press
    // to save the image. Direct attribute "download" is unreliable cross-origin.
    window.open(ticketUrl, '_blank', 'noopener,noreferrer');
  };
  if (loading) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6" data-id="element-8442">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" data-id="element-8443" />
        <p className="text-sm text-gray-500" data-id="element-8444">Cargando ticket…</p>
        {retried > 0 && <p className="text-xs text-gray-400 mt-1" data-id="element-8445">
            Generando ticket… intento {retried}/6
          </p>}
      </div>;
  }
  if (error || !ticketUrl) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6" data-id="element-8446">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg" data-id="element-8447">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" data-id="element-8448" />
          <h1 className="text-xl font-black text-gray-900 mb-1" data-id="element-8449">
            Ticket no disponible
          </h1>
          <p className="text-sm text-gray-500 mb-4" data-id="element-8450">
            {error || 'No pudimos encontrar este ticket.'}
          </p>
          {orderNumber && <p className="text-xs text-gray-400 mb-4" data-id="element-8451">
              Orden: <span className="font-mono" data-id="element-8452">{orderNumber}</span>
            </p>}
          <button onClick={() => {
          setError(null);
          setLoading(true);
          setRetried(0);
        }} className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700 transition-colors" data-id="element-8453">
            Reintentar
          </button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-100 flex flex-col items-center py-6 px-4" data-id="element-8454">
      <div className="w-full max-w-md" data-id="element-8455">
        {/* Header */}
        <div className="bg-white rounded-t-2xl px-5 py-4 border-b border-gray-100 shadow-sm" data-id="element-8456">
          <h1 className="text-base font-black text-gray-900" data-id="element-8457">
            🧾 Ticket del pedido
          </h1>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-8458">
            {restaurantName ? `${restaurantName} · ` : ''}
            <span className="font-mono" data-id="element-8459">{orderNumber}</span>
          </p>
        </div>

        {/* Image */}
        <div className="bg-white shadow-sm flex items-center justify-center p-3" data-id="element-8460">
          <img src={ticketUrl} alt={`Ticket ${orderNumber}`} className="w-full h-auto rounded-md" decoding="async" data-id="element-8461" />
        </div>

        {/* Actions */}
        <div className="bg-white rounded-b-2xl px-5 py-4 border-t border-gray-100 shadow-sm flex flex-col gap-2" data-id="element-8462">
          <button onClick={handleDownload} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-700 transition-colors" data-id="element-8463">
            <Download className="w-4 h-4" data-id="element-8464" />
            Abrir / Descargar imagen
          </button>
          <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors" data-id="element-8465">
            <ExternalLink className="w-4 h-4" data-id="element-8466" />
            Ver en nueva pestaña
          </a>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-4" data-id="element-8467">
          Servido por NOIZA
        </p>
      </div>
    </div>;
}